// URL Constants for Production Configuration
// All URLs should be configurable via environment variables

export const EXTERNAL_URLS = {
	// Solana Price API - Use local API route to avoid CORS
	COINGECKO_SOL_PRICE:
		process.env.NEXT_PUBLIC_COINGECKO_API_URL || "/api/price/sol",

	// Blockchain Explorer
	SOLSCAN_BASE:
		process.env.NEXT_PUBLIC_SOLSCAN_URL || "https://solscan.io/token",

	// Legacy WebSocket URLs (for backward compatibility)
	WEBSOCKET_URL:
		process.env.NEXT_PUBLIC_WS_URL ||
		(process.env.NEXT_PUBLIC_ENV === "local"
			? "wss://micro-transfer.mygeotokens.com/dev/ws"
			: "wss://micro-transfer.mygeotokens.com/ws"),

	// Socket.IO URLs
	SOCKETIO_URL:
		process.env.NEXT_PUBLIC_SOCKETIO_URL ||
		(process.env.NEXT_PUBLIC_ENV === "local"
			? "https://micro-transfer.mygeotokens.com/dev"
			: "https://micro-transfer.mygeotokens.com"),

	// Image CDN Services
	IPFS_GATEWAY:
		process.env.NEXT_PUBLIC_IPFS_GATEWAY || "https://cf-ipfs.com/ipfs",
	MAGIC_EDEN_CDN:
		process.env.NEXT_PUBLIC_MAGIC_EDEN_CDN ||
		"https://img-cdn.magiceden.dev/rs:fill",
	IMAGE_PROXY:
		process.env.NEXT_PUBLIC_IMAGE_PROXY || "https://images.weserv.nl",

	// Company Links
	DISCORD:
		process.env.NEXT_PUBLIC_DISCORD_URL || "https://discord.gg/UpxTdUVkKy",
	GITBOOK:
		process.env.NEXT_PUBLIC_GITBOOK_URL || "https://gitbook.mygeotokens.com",

	// Contact Information
	SUPPORT_DISCORD:
		process.env.NEXT_PUBLIC_DISCORD_URL || "https://discord.gg/UpxTdUVkKy",
	PRIVACY_EMAIL:
		process.env.NEXT_PUBLIC_PRIVACY_EMAIL || "privacy@mygeotokens.com",
} as const;

// Helper functions for URL construction
export const buildSolscanUrl = (
	tokenAddress: string,
	network?: string
): string => {
	const networkParam = network === "devnet" ? "?cluster=devnet" : "";
	return `${EXTERNAL_URLS.SOLSCAN_BASE}/${tokenAddress}${networkParam}`;
};

export const buildIpfsUrl = (
	hash: string,
	width?: number,
	height?: number,
	quality?: number
): string => {
	const params =
		width && height && quality
			? `?width=${width}&height=${height}&quality=${quality}`
			: "";
	return `${EXTERNAL_URLS.IPFS_GATEWAY}/${hash}${params}`;
};

export const buildMagicEdenUrl = (
	originalUrl: string,
	width: number,
	height: number
): string => {
	return `${EXTERNAL_URLS.MAGIC_EDEN_CDN}:${width}:${height}:0:0/plain/${originalUrl}`;
};

export const buildImageProxyUrl = (
	url: string,
	width?: number,
	height?: number,
	quality?: number
): string => {
	const encodedUrl = encodeURIComponent(url);
	const params = new URLSearchParams();
	params.append("url", url);
	if (width) params.append("w", width.toString());
	if (height) params.append("h", height.toString());
	if (quality) params.append("q", quality.toString());
	params.append("fit", "cover");

	return `${EXTERNAL_URLS.IMAGE_PROXY}/?${params.toString()}`;
};

// Legacy WebSocket URL helpers (for backward compatibility)
export const getWebSocketUrl = (): string => {
	return EXTERNAL_URLS.WEBSOCKET_URL;
};

// Socket.IO URL helpers
export const getSocketIOUrl = (): string => {
	return EXTERNAL_URLS.SOCKETIO_URL;
};

// Utility to get the appropriate URL based on connection type
export const getConnectionUrl = (useSocketIO: boolean = true): string => {
	return useSocketIO ? getSocketIOUrl() : getWebSocketUrl();
};

// Development vs Production URL helpers
export const getCoinGeckoUrl = (): string => {
	return EXTERNAL_URLS.COINGECKO_SOL_PRICE;
};

// Alternative CoinGecko endpoints (if direct API is needed)
export const getAlternativeCoinGeckoUrl = (): string => {
	// Try the newer API endpoint with better CORS support
	return "https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd&x_cg_demo_api_key=CG-Demo";
};

// Socket.IO specific configuration
export const getSocketIOConfig = () => {
	return {
		url: getSocketIOUrl(),
		path: process.env.NEXT_PUBLIC_SOCKETIO_PATH || "/ws/socket.io/",
		transports: ["websocket", "polling"],
		autoConnect: true,
		reconnection: true,
		reconnectionAttempts: 5,
		reconnectionDelay: 1000,
		reconnectionDelayMax: 30000,
		timeout: 10000,
	};
};
